(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  class MirzaSlider extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      this.paused = reducedMotion.matches;
      this.hovered = false;
      this.focused = false;
      this.motion = this.querySelector('[data-motion]');
      if (this.slides.length < 2) return;
      this.querySelector('[data-controls]').hidden = false;
      this.querySelector('[data-prev]').addEventListener('click', () => this.show(this.index - 1), options);
      this.querySelector('[data-next]').addEventListener('click', () => this.show(this.index + 1), options);
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.show(i), options));
      this.motion.addEventListener('click', () => { this.paused = !this.paused; this.syncMotion(); }, options);
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hovered = true; this.schedule(); } }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', () => { queueMicrotask(() => { this.focused = this.contains(document.activeElement); this.schedule(); }); }, options);
      this.addEventListener('touchstart', e => { this.touch = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }; }, { ...options, passive: true });
      this.addEventListener('touchend', e => {
        if (!this.touch) return;
        const x = e.changedTouches[0].clientX - this.touch.x;
        const y = e.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.5) this.show(this.index + (x < 0 ? 1 : -1));
        this.touch = null;
      }, { ...options, passive: true });
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      reducedMotion.addEventListener('change', () => { this.paused = reducedMotion.matches; this.syncMotion(); }, options);
      document.addEventListener('shopify:block:select', e => {
        const slide = e.target.closest('[data-slide]');
        const i = this.slides.indexOf(slide);
        if (i >= 0) { this.paused = true; this.show(i); this.syncMotion(); }
      }, options);
      this.syncMotion();
    }
    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.hidden = !active;
        slide.inert = !active;
        slide.classList.toggle('is-active', active);
        this.dots[i].setAttribute('aria-pressed', String(active));
      });
      this.schedule();
    }
    syncMotion() {
      this.motion.setAttribute('aria-label', this.paused ? this.motion.dataset.play : this.motion.dataset.pause);
      this.motion.setAttribute('aria-pressed', String(this.paused));
      this.motion.querySelector('span').textContent = this.paused ? '▷' : 'Ⅱ';
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.paused && !this.hovered && !this.focused && !document.hidden && this.isConnected) this.timer = setTimeout(() => this.show(this.index + 1), 4800);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); this.controller = null; }
  }
  class MirzaTicker extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const button = this.querySelector('[data-motion]');
      button.hidden = false;
      this.paused = reducedMotion.matches;
      const sync = () => {
        this.classList.toggle('is-paused', this.paused);
        button.setAttribute('aria-pressed', String(this.paused));
        button.setAttribute('aria-label', this.paused ? button.dataset.play : button.dataset.pause);
        button.querySelector('span').textContent = this.paused ? '▷' : 'Ⅱ';
      };
      button.addEventListener('click', () => { this.paused = !this.paused; sync(); }, { signal: this.controller.signal });
      reducedMotion.addEventListener('change', () => { this.paused = reducedMotion.matches; sync(); }, { signal: this.controller.signal });
      sync();
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  class MirzaFinder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.querySelector('[data-finder-submit]').hidden = false;
      this.querySelector('.mirza-finder__fallback').hidden = true;
      this.querySelector('form').addEventListener('submit', e => {
        e.preventDefault();
        const value = new FormData(e.currentTarget).get('need');
        const source = this.querySelector(`[data-match="${value === 'brush' ? 'brush' : 'roller'}"]`);
        this.querySelector('[data-title]').textContent = source.dataset.title;
        this.querySelector('[data-result-link]').href = source.href;
        this.querySelector('[data-result]').hidden = false;
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  class MirzaDuo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add]');
      this.status = this.querySelector('[data-status]');
      this.form.addEventListener('submit', e => this.add(e), { signal: this.controller.signal });
      this.form.addEventListener('change', () => {
        const regular = [...this.form.querySelectorAll('select')].reduce((sum, select) => sum + Number(select.selectedOptions[0]?.dataset.price || 0), 0);
        const discount = Number(this.dataset.discount || 0);
        const bundle = Math.max(0, regular - discount);
        const money = value => new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(value / 100);
        const regularEl = this.querySelector('[data-regular-total]');
        if (regularEl) regularEl.textContent = money(regular);
        this.querySelector('[data-total]').textContent = money(bundle);
      }, { signal: this.controller.signal });
    }
    async add(event) {
      event.preventDefault();
      if (this.button.disabled) return;
      const selections = [...this.form.querySelectorAll('select')];
      if (selections.some(select => select.disabled || !select.selectedOptions[0] || select.selectedOptions[0].disabled)) {
        this.status.textContent = this.dataset.unavailable;
        return;
      }
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      let added = false;
      try {
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const payload = { items: selections.map(select => ({ id: Number(select.value), quantity: 1 })) };
        if (cart?.getSectionsToRender) {
          payload.sections = cart.getSectionsToRender().map(section => section.id);
          payload.sections_url = window.location.pathname;
          cart.setActiveElement(this.button);
        }
        const response = await fetch(this.dataset.addUrl + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        added = true;
        if (cart?.renderContents && result.sections && payload.sections.every(id => typeof result.sections[id] === 'string')) {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
        } else {
          window.location.assign(this.dataset.cartUrl);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mirza-duo', cartData: result });
      } catch (error) {
        if (added) window.location.assign(this.dataset.cartUrl);
        else this.status.textContent = error.message || this.dataset.error;
      } finally {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  for (const [name, element] of [['mirza-slider', MirzaSlider], ['mirza-ticker', MirzaTicker], ['mirza-finder', MirzaFinder], ['mirza-duo', MirzaDuo]]) {
    if (!customElements.get(name)) customElements.define(name, element);
  }
  const logoFallbacks = (root = document) => root.querySelectorAll('[data-mirza-logo]').forEach(img => {
    const fallback = () => { img.hidden = true; img.nextElementSibling.hidden = false; };
    if (img.complete && !img.naturalWidth) fallback();
    else img.addEventListener('error', fallback, { once: true });
  });
  logoFallbacks();
  document.addEventListener('shopify:section:load', e => logoFallbacks(e.target));
})();
